import { useEffect, useState, type RefObject } from 'react';
import { ChevronRight } from 'lucide-react';
import roomStyles from '../Room.module.css';
import styles from './IntroRoom.module.css';

const rotatingSkills = [
  'build full-stack systems',
  'ship React experiences',
  'design APIs and data',
  'build RAG pipelines',
  'orchestrate AI agents',
];

type IntroRoomProps = {
  sectionRef: RefObject<HTMLElement | null>;
  showIdleHint: boolean;
};

export function IntroRoom({ sectionRef, showIdleHint }: IntroRoomProps) {
  const [typedSkill, setTypedSkill] = useState('');

  useEffect(() => {
    let skillIndex = 0;
    let deleting = false;
    let pauseTicks = 0;
    let text = '';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const reducedMotionTimer = window.setTimeout(() => setTypedSkill(rotatingSkills[0]), 0);
      return () => window.clearTimeout(reducedMotionTimer);
    }

    const timer = window.setInterval(() => {
      const target = rotatingSkills[skillIndex];
      if (pauseTicks > 0) {
        pauseTicks -= 1;
        return;
      }
      if (!deleting) {
        text = target.slice(0, text.length + 1);
        if (text === target) {
          deleting = true;
          pauseTicks = 10;
        }
      } else {
        text = text.slice(0, -1);
        if (!text) {
          deleting = false;
          skillIndex = (skillIndex + 1) % rotatingSkills.length;
          pauseTicks = 2;
        }
      }
      setTypedSkill(text);
    }, 72);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`${roomStyles.room} ${roomStyles.wideRoom} ${styles.room}`}
      aria-label="Pixel-art introduction room"
    >
      <div className={roomStyles.scene}>
        <picture className={roomStyles.background}>
          <source media="(max-width: 680px)" srcSet="/assets/v5-room-intro-mobile.png" />
          <source media="(min-width: 681px)" srcSet="/assets/v5-room-intro-ultrawide.png" />
          <img
            src="/assets/v5-room-intro-clean.png"
            alt="Pixel-art computer desk with a large CRT monitor, keyboard, steaming coffee and a sleeping gray tabby cat"
          />
        </picture>
        <div className={styles.crtCopy}>
          <span>SYSTEM READY_</span>
          <h1>
            Hi, I am
            <br />
            <strong>Martin Andreev.</strong>
          </h1>
          <p>
            I can <b>{typedSkill}</b>
            <i aria-hidden="true" />
          </p>
          <small>
            SCROLL TO EXPLORE <ChevronRight size={12} />
          </small>
          {showIdleHint && (
            <output className={styles.idlePrompt}>
              <span>CONTINUE EXPLORING</span>
              <b>
                SCROLL ↓ OR
                <br />
                USE THE ROOM MENU
              </b>
            </output>
          )}
        </div>
        <div className={styles.steamSprite} aria-hidden="true" />
        <div className={styles.catSprite} aria-hidden="true" />
      </div>
    </section>
  );
}
